import { useState } from "react";
import { Button, Input } from "@shop-ledger/ui-components";
import { usePlatformClient } from "../../api/ClientContext";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import type { PlatformGeneralSettingsUpdate } from "../../api/types";

interface FormState {
  smtp_host: string;
  smtp_port: string;
  smtp_username: string;
  smtp_password: string;
  smtp_from_email: string;
  smtp_from_name: string;
  smtp_use_tls: boolean;
}

const EMPTY_FORM: FormState = {
  smtp_host: "",
  smtp_port: "",
  smtp_username: "",
  smtp_password: "",
  smtp_from_email: "",
  smtp_from_name: "",
  smtp_use_tls: true,
};

/**
 * §9.7 General Settings — platform-wide outbound SMTP config, the config every tenant-
 * facing feature that eventually sends email (receipts, notifications, ...) will read from
 * `PlatformSettings` server-side. This page is deliberately the FIRST and only caller of
 * that config today: it exists so an operator can prove SMTP actually works (the "Send
 * test email" button) before anything else depends on it.
 *
 * The password field always starts blank on load, even when one is already saved
 * server-side — `smtp_password_set` (never the real value) is all `GET` ever returns, so
 * "blank" here means "unchanged" on save, not "no password configured." The placeholder
 * text is how that distinction is surfaced to the operator.
 */
export function GeneralSettingsPage() {
  const client = usePlatformClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [passwordIsSet, setPasswordIsSet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [testPending, setTestPending] = useState(false);
  const [testResult, setTestResult] = useState<{ sent: boolean; detail: string } | null>(null);

  const { loading, error } = useAsyncData(async () => {
    const settings = await client.getGeneralSettings();
    setForm({
      smtp_host: settings.smtp_host ?? "",
      smtp_port: settings.smtp_port ? String(settings.smtp_port) : "",
      smtp_username: settings.smtp_username ?? "",
      smtp_password: "",
      smtp_from_email: settings.smtp_from_email ?? "",
      smtp_from_name: settings.smtp_from_name ?? "",
      smtp_use_tls: settings.smtp_use_tls,
    });
    setPasswordIsSet(settings.smtp_password_set);
    return settings;
  }, [client]);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaveSuccess(false);
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const body: PlatformGeneralSettingsUpdate = {
        smtp_host: form.smtp_host,
        smtp_port: form.smtp_port ? Number(form.smtp_port) : undefined,
        smtp_username: form.smtp_username,
        smtp_from_email: form.smtp_from_email,
        smtp_from_name: form.smtp_from_name,
        smtp_use_tls: form.smtp_use_tls,
      };
      // Blank password field means "leave it as-is" — only send it when the operator
      // actually typed something, per PlatformGeneralSettingsUpdate's doc comment.
      if (form.smtp_password) body.smtp_password = form.smtp_password;

      const updated = await client.updateGeneralSettings(body);
      setPasswordIsSet(updated.smtp_password_set);
      setForm((prev) => ({ ...prev, smtp_password: "" }));
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Could not save settings.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSendTest() {
    setTestPending(true);
    setTestResult(null);
    try {
      const result = await client.sendTestEmail(testEmailAddress);
      setTestResult(result);
    } catch (err) {
      setTestResult({ sent: false, detail: err instanceof Error ? err.message : "Request failed." });
    } finally {
      setTestPending(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">General Settings</h1>
          <p className="page__subtitle">Platform-wide outbound email (SMTP) configuration.</p>
        </div>
      </div>

      {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}
      {saveError ? <StatusBanner kind="error">{saveError}</StatusBanner> : null}
      {saveSuccess ? <StatusBanner kind="info">Settings saved.</StatusBanner> : null}

      <div className="panel">
        <h2 className="panel__title">Where to get these values</h2>
        <p className="muted" style={{ marginBottom: 8 }}>
          This platform sends mail through the self-hosted Mailcow instance at{" "}
          <a href="https://mail.electro-genius.com" target="_blank" rel="noreferrer">
            mail.electro-genius.com
          </a>
          . To get credentials for a sending mailbox (e.g. <code>noreply@electro-genius.com</code>):
        </p>
        <ol className="muted" style={{ marginBottom: 0, paddingLeft: 20 }}>
          <li>
            Log into the Mailcow admin UI above with your admin account, then go to{" "}
            <strong>Configuration → Mail Setup → Mailboxes</strong>.
          </li>
          <li>
            Use an existing mailbox, or click <strong>Add mailbox</strong> to create a dedicated one for
            sending (recommended over reusing a personal inbox, so it can be revoked independently).
          </li>
          <li>
            Open that mailbox, go to the <strong>App Passwords</strong> tab, and generate one scoped to{" "}
            <strong>SMTP</strong>. Use the mailbox's full address as the <em>Username</em> below and the
            generated app password as the <em>Password</em> — not the mailbox's real login password.
          </li>
          <li>
            <strong>Host:</strong> <code>mail.electro-genius.com</code> &nbsp;·&nbsp;{" "}
            <strong>Port:</strong> <code>587</code> (STARTTLS) or <code>465</code> (implicit TLS) &nbsp;·&nbsp;{" "}
            <strong>From address:</strong> the same mailbox address.
          </li>
        </ol>
      </div>

      <div className="panel">
        <h2 className="panel__title">SMTP</h2>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, maxWidth: 640 }}>
            <Input
              label="SMTP host"
              placeholder="mail.electro-genius.com"
              value={form.smtp_host}
              onChange={(e) => update("smtp_host", e.target.value)}
            />
            <Input
              label="Port"
              type="number"
              placeholder="587"
              value={form.smtp_port}
              onChange={(e) => update("smtp_port", e.target.value)}
            />
            <Input
              label="Username"
              placeholder="noreply@electro-genius.com"
              value={form.smtp_username}
              onChange={(e) => update("smtp_username", e.target.value)}
            />
            <Input
              label="Password"
              type="password"
              placeholder={passwordIsSet ? "•••••••• (unchanged — leave blank to keep)" : "Not set"}
              value={form.smtp_password}
              onChange={(e) => update("smtp_password", e.target.value)}
            />
            <Input
              label="From address"
              placeholder="noreply@electro-genius.com"
              value={form.smtp_from_email}
              onChange={(e) => update("smtp_from_email", e.target.value)}
            />
            <Input
              label="From name"
              placeholder="ZEL-MART"
              value={form.smtp_from_name}
              onChange={(e) => update("smtp_from_name", e.target.value)}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, gridColumn: "1 / -1" }}>
              <input
                type="checkbox"
                checked={form.smtp_use_tls}
                onChange={(e) => update("smtp_use_tls", e.target.checked)}
              />
              Use TLS (STARTTLS on 587, or implicit TLS automatically on 465)
            </label>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <Button type="button" variant="primary" size="md" disabled={saving || loading} onClick={() => void handleSave()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="panel">
        <h2 className="panel__title">Send a test email</h2>
        <p className="muted" style={{ marginBottom: 14 }}>
          Sends against whatever is currently saved above — save first if you just changed anything.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", maxWidth: 480 }}>
          <div style={{ flex: 1 }}>
            <Input
              label="Send to"
              type="email"
              placeholder="you@example.com"
              value={testEmailAddress}
              onChange={(e) => setTestEmailAddress(e.target.value)}
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            size="md"
            disabled={testPending || !testEmailAddress}
            onClick={() => void handleSendTest()}
          >
            {testPending ? "Sending…" : "Send test email"}
          </Button>
        </div>
        {testResult ? (
          <div style={{ marginTop: 14 }}>
            <StatusBanner kind={testResult.sent ? "info" : "error"}>{testResult.detail}</StatusBanner>
          </div>
        ) : null}
      </div>
    </div>
  );
}
